// Sidebar navigation (store admin). Icons are lucide-react names. `perm` hides the item from
// staff without that permission. Owned by the shell; modules do not edit this file.
import type { Permission } from '@shared/types'

export interface NavItem {
  label: string; to: string; icon: string; perm?: Permission; children?: Array<{ label: string; to: string }>
  /** A live count next to the label (sidebar fetches it; hidden at 0). */
  badge?: 'inbox_unread'
}
export interface NavSection { label?: string; items: NavItem[] }

export const storeNav: NavSection[] = [
  { items: [
    { label: 'Home', to: '/', icon: 'House' },
    { label: 'Orders', to: '/orders', icon: 'ShoppingBag', perm: 'orders', children: [
      { label: 'Abandoned checkouts', to: '/checkouts' }, { label: 'Returns', to: '/returns' }, { label: 'Shipping labels', to: '/shipping-labels' },
      { label: 'Delivery issues', to: '/delivery-issues' } ] },
    { label: 'Inbox', to: '/inbox', icon: 'MessagesSquare', perm: 'inbox', badge: 'inbox_unread' },
    { label: 'Products', to: '/products', icon: 'Tag', perm: 'products', children: [
      { label: 'Collections', to: '/collections' }, { label: 'Inventory', to: '/inventory' }, { label: 'Bulk editor', to: '/products/bulk' } ] },
    { label: 'Customers', to: '/customers', icon: 'Users', perm: 'customers' },
    { label: 'Subscriptions', to: '/subscriptions', icon: 'Repeat', perm: 'subscriptions', children: [{ label: 'Plans', to: '/subscriptions/plans' }] },
    { label: 'Marketing', to: '/marketing', icon: 'Megaphone', perm: 'marketing', children: [
      { label: 'Campaigns', to: '/marketing/campaigns' }, { label: 'Automations', to: '/marketing/automations' },
      { label: 'Segments', to: '/marketing/segments' }, { label: 'Loyalty', to: '/marketing/loyalty' },
      { label: 'Referrals', to: '/marketing/referrals' }, { label: 'Affiliates', to: '/marketing/affiliates' },
      { label: 'Ad spend', to: '/marketing/ad-spend' }, { label: 'Channels', to: '/marketing/channels' },
      { label: 'WhatsApp queue', to: '/marketing/whatsapp' }, { label: 'Contacts', to: '/marketing/contacts' } ] },
    { label: 'Discounts', to: '/discounts', icon: 'BadgePercent', perm: 'discounts' },
    { label: 'Reviews', to: '/reviews', icon: 'Star', perm: 'reviews' },
    { label: 'Analytics', to: '/analytics', icon: 'ChartLine', perm: 'analytics', children: [{ label: 'Profit', to: '/analytics/profit' }] },
  ] },
  { label: 'Sales channels', items: [
    { label: 'Online store', to: '/online-store', icon: 'Store', perm: 'online_store', children: [
      { label: 'Themes', to: '/online-store' }, { label: 'Pages', to: '/online-store/pages' },
      { label: 'Navigation', to: '/online-store/navigation' }, { label: 'Domains', to: '/online-store/domains' },
      { label: 'Translate', to: '/online-store/translate' }, { label: 'Preferences', to: '/online-store/preferences' } ] },
  ] },
  { items: [
    { label: 'Import', to: '/import', icon: 'Import', perm: 'products' },
    { label: 'Settings', to: '/settings', icon: 'Settings' },
  ] },
]

export const platformNav: NavSection[] = [
  { items: [
    { label: 'Overview', to: '/platform', icon: 'Gauge' },
    { label: 'Stores', to: '/platform/stores', icon: 'Store' },
    { label: 'Risk review', to: '/platform/risk', icon: 'ShieldAlert' },
    { label: 'Abuse reports', to: '/platform/reports', icon: 'Flag' },
    { label: 'Plans', to: '/platform/plans', icon: 'Layers' },
    { label: 'Invoices', to: '/platform/invoices', icon: 'Receipt' },
    { label: 'Users', to: '/platform/users', icon: 'UserCog' },
    { label: 'Jobs', to: '/platform/jobs', icon: 'Cog' },
    { label: 'WhatsApp', to: '/platform/whatsapp', icon: 'MessagesSquare' },
    { label: 'Settings', to: '/platform/settings', icon: 'Settings' },
  ] },
]

/** Settings hub cards (settings/SettingsHome renders these). */
export const settingsSections = [
  { label: 'General', to: '/settings/general', icon: 'Store', description: 'Store name, address, currency and time zone' },
  { label: 'Plan & billing', to: '/settings/plan', icon: 'CreditCard', description: 'Your plan, invoices and AI credits', perm: 'billing' },
  { label: 'Payments', to: '/settings/payments', icon: 'Wallet', description: 'Gateways, cash on delivery and bank transfer', perm: 'payments' },
  { label: 'Checkout', to: '/settings/checkout', icon: 'ShoppingCart', description: 'Checkout fields, customer accounts and order processing' },
  { label: 'Shipping & delivery', to: '/settings/shipping', icon: 'Truck', description: 'Zones, rates and local pickup' },
  { label: 'Taxes', to: '/settings/taxes', icon: 'Landmark', description: 'Tax rates by country and region' },
  { label: 'Notifications', to: '/settings/notifications', icon: 'Mail', description: 'Customer emails and staff alerts' },
  { label: 'Staff', to: '/settings/staff', icon: 'UserPlus', description: 'Invite people and set permissions', perm: 'staff' },
  { label: 'Custom fields', to: '/settings/custom-fields', icon: 'ListPlus', description: 'Extra fields for products, customers and orders' },
  { label: 'Policies', to: '/settings/policies', icon: 'FileText', description: 'Refund, shipping, privacy and terms' },
  { label: 'Markets', to: '/settings/markets', icon: 'Globe', description: 'Currencies and languages your customers see' },
  { label: 'Carriers & labels', to: '/settings/carriers', icon: 'Package', description: 'Courier accounts, shipping labels, pickups and tracking' },
  { label: 'Returns & exchanges', to: '/settings/returns', icon: 'Undo2', description: 'Return window, reasons and the self-serve returns page' },
  { label: 'Costs & fees', to: '/settings/costs', icon: 'Calculator', description: 'Product costs, gateway fees and fixed costs for profit reports' },
  { label: 'Chat & AI agent', to: '/settings/inbox', icon: 'Bot', description: 'Live chat on your store and the AI support agent' },
] as const
